'use client';

import { useRef, useState } from 'react';
import { Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { validateAssetFilePolicy } from '@impact/assets';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button/button';
import { ImageCropDialog } from '@/components/image-crop-dialog';
import { cn } from '@/lib/utils';
import { canRenderInBrowserPreview } from '../lib/asset-preview';
import { convertHeicToJpeg } from '../lib/convert-heic';

const DEFAULT_IMAGE_ACCEPT =
  'image/jpeg,image/png,image/webp,image/avif,image/gif,image/svg+xml,image/heic,image/heif,image/heic-sequence,image/heif-sequence';

interface AssetImageUploadFieldClassNames {
  root?: string;
  dropzone?: string;
  previewImage?: string;
  info?: string;
  button?: string;
  helperText?: string;
  removeButton?: string;
  fallbackText?: string;
  emptyState?: string;
}

interface AssetImageUploadFieldProps {
  valueUrl: string | null;
  mimeType?: string | null;
  previewName?: string | null;
  previewAlt?: string;
  accept?: string;
  helperText?: string;
  buttonLabel?: string;
  emptyLabel?: string;
  heicFallbackLabel?: string;
  layout?: 'horizontal' | 'vertical';
  showButton?: boolean;
  openOnPreviewClick?: boolean;
  validatePolicy?: boolean;
  disabled?: boolean;
  isUploading?: boolean;
  onFileSelected: (file: File) => void | Promise<void>;
  onRemove: () => void;
  onValidationError?: (message: string) => void;
  onFileSelectionError?: (error: unknown) => void;
  classNames?: AssetImageUploadFieldClassNames;
  crop?: {
    aspect?: number;
    outputSize?: number;
    outputType?: string;
    outputQuality?: number;
  };
}

export function AssetImageUploadField({
  valueUrl,
  mimeType,
  previewName,
  previewAlt = 'Asset preview',
  accept = DEFAULT_IMAGE_ACCEPT,
  helperText,
  buttonLabel = 'Upload Image',
  emptyLabel = 'Upload',
  heicFallbackLabel = 'HEIC preview unavailable',
  layout = 'horizontal',
  showButton = true,
  openOnPreviewClick = true,
  validatePolicy = true,
  disabled = false,
  isUploading = false,
  onFileSelected,
  onRemove,
  onValidationError,
  onFileSelectionError,
  classNames,
  crop,
}: AssetImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const canPreview = canRenderInBrowserPreview({
    mimeType,
    urlOrName: previewName ?? valueUrl,
  });

  const triggerFilePicker = () => {
    if (disabled || isUploading) return;
    inputRef.current?.click();
  };

  return (
    <div
      className={cn(
        'flex items-start gap-4',
        layout === 'vertical' ? 'flex-col' : 'flex-row',
        classNames?.root,
      )}
    >
      <div
        className={cn(
          'relative h-24 w-24 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 overflow-visible',
          openOnPreviewClick && !(disabled || isUploading) && 'cursor-pointer hover:bg-muted',
          classNames?.dropzone,
        )}
        onClick={openOnPreviewClick ? triggerFilePicker : undefined}
      >
        <div className="absolute inset-0 rounded-[6px] overflow-hidden flex items-center justify-center">
          {isUploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : valueUrl && canPreview ? (
            <img
              src={valueUrl}
              alt={previewAlt}
              className={cn('h-full w-full object-contain', classNames?.previewImage)}
            />
          ) : valueUrl ? (
            <div className={cn('text-center px-2', classNames?.fallbackText)}>
              <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground/50" />
              <span className="text-[10px] text-muted-foreground mt-1 block">
                {heicFallbackLabel}
              </span>
            </div>
          ) : (
            <div className={cn('text-center', classNames?.emptyState)}>
              <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <span className="text-xs text-muted-foreground mt-1 block">{emptyLabel}</span>
            </div>
          )}
        </div>

        {!isUploading && valueUrl && (
          <button
            type="button"
            className={cn(
              'absolute top-1 right-1 z-20 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-sm hover:bg-destructive/90',
              classNames?.removeButton,
            )}
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        disabled={disabled || isUploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;

          const run = async () => {
            const processedFile = await convertHeicToJpeg(file);

            if (validatePolicy) {
              const validation = validateAssetFilePolicy({
                file: processedFile,
                assetType: 'image',
              });
              if (!validation.ok) {
                const message = validation.reason ?? 'Invalid image file';
                if (onValidationError) {
                  onValidationError(message);
                } else {
                  toast.error(message);
                }
                return;
              }
            }

            if (crop) {
              setCropSrc(URL.createObjectURL(processedFile));
              return;
            }

            await onFileSelected(processedFile);
          };

          void run()
            .catch((error) => {
              if (onFileSelectionError) {
                onFileSelectionError(error);
                return;
              }

              const message =
                error instanceof Error && error.message ? error.message : 'Failed to select file';
              toast.error(message);
            })
            .finally(() => {
              if (inputRef.current) inputRef.current.value = '';
            });
        }}
      />

      <div className={cn('space-y-2', classNames?.info)}>
        {showButton && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={classNames?.button}
            onClick={triggerFilePicker}
            disabled={disabled || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {buttonLabel}
              </>
            )}
          </Button>
        )}
        {helperText && (
          <p className={cn('text-xs text-muted-foreground', classNames?.helperText)}>
            {helperText}
          </p>
        )}
      </div>

      {crop && cropSrc && (
        <ImageCropDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              URL.revokeObjectURL(cropSrc);
              setCropSrc(null);
            }
          }}
          imageSrc={cropSrc}
          aspect={crop.aspect}
          outputSize={crop.outputSize}
          outputType={crop.outputType}
          outputQuality={crop.outputQuality}
          onCropped={async (croppedFile) => {
            URL.revokeObjectURL(cropSrc);
            setCropSrc(null);
            await onFileSelected(croppedFile);
          }}
        />
      )}
    </div>
  );
}
